import theme from "Theme/AppTheme";
import type { FilterKey } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import type {
    CategoricalGroupByOption,
    GroupByOptionValue,
} from "../../../SignalsModels";

export type GroupByOptionPresentation = {
    /**
     * Links the option to its filter-picker entry, which supplies its menu group, position, title
     * and access rules -- so the Group By menu reads the same way the filter picker does.
     *
     * The access decision is `canAccessFilterItem`'s, which is the only place that knows about
     * inverted rules like Restrict Evaluations hiding module scores from non-ultra users.
     */
    filterKey?: FilterKey;
    /**
     * Menu title for an option whose filter entry is titled for the filter rather than the
     * grouping. Grouping by Date picks a grain inside the Date Range filter's window, so it takes
     * that filter's place in the menu without taking its name.
     */
    menuTitle?: string;
    /** Menu group used when the option has no filter-picker entry to inherit one from. */
    fallbackGroupLabel?: string;
    /** Singular noun for the value pickers ("Agent"). */
    itemName: string;
    /** Plural label above the value pickers ("Agents"). */
    valueLabel: string;
};

export const configureInputLabelSx = {
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: "12px",
    lineHeight: "16px",
};

export const GROUP_BY_OPTION_CONFIG: Record<
    GroupByOptionValue,
    GroupByOptionPresentation
> = {
    Agent: {
        filterKey: "agentIds",
        itemName: "Agent",
        valueLabel: "Agents",
    },
    AgentManager: {
        filterKey: "agentManagerIds",
        itemName: "Agent Manager",
        valueLabel: "Agent Managers",
    },
    Hierarchy: {
        filterKey: "hierarchyIds",
        itemName: "Hierarchy",
        valueLabel: "Hierarchies",
    },
    ConversationLabeling: {
        filterKey: "dataLabels",
        itemName: "Conversation Label",
        valueLabel: "Conversation Labeling",
    },
    EvaluationType: {
        filterKey: "evaluationTypes",
        itemName: "Evaluation Type",
        valueLabel: "Evaluation Type",
    },
    Language: {
        filterKey: "languages",
        itemName: "Language",
        valueLabel: "Language",
    },
    MediaType: {
        filterKey: "mediaTypes",
        itemName: "Media Type",
        valueLabel: "Media Type",
    },
    // The two Theme options share a title in the menu and are told apart by their status tag.
    // itemName and valueLabel land on props typed `string`, which cannot carry a tag, so the
    // deprecated one picks up a "(Deprecated)" marker at the point of use -- only for viewers who
    // can see that status. See ApplicationFilterItemsStore.withDeprecationMarker.
    PrimaryThemes: {
        filterKey: "topics",
        itemName: "Primary Theme",
        valueLabel: "Primary Themes",
    },
    Themes: {
        filterKey: "themes",
        itemName: "Primary Theme",
        valueLabel: "Primary Themes",
    },
    Outline: {
        filterKey: "outlineSegmentFilterItems",
        itemName: "Outline",
        valueLabel: "Outline",
    },
    PrimaryTopic: {
        filterKey: "primaryTopics",
        itemName: "Primary Topic",
        valueLabel: "Primary Topics",
    },
    PrimaryDriver: {
        filterKey: "drivers",
        itemName: "Primary Driver",
        valueLabel: "Primary Drivers",
    },
    PatientJourney: {
        filterKey: "patientJourneyStages",
        itemName: "Patient Journey",
        valueLabel: "Patient Journey",
    },
    EddyEffect: {
        filterKey: "eddyEffectStatus",
        itemName: "Eddy Signal",
        valueLabel: "Eddy Signal",
    },
    CallDirection: {
        filterKey: "callDirections",
        itemName: "Call Direction",
        valueLabel: "Call Direction",
    },
    ContactType: {
        filterKey: "contactTypes",
        itemName: "Contact Type",
        valueLabel: "Contact Type",
    },
    Classifiers: {
        filterKey: "rbcFilterItems",
        itemName: "Classifier",
        valueLabel: "Classifiers",
    },
    ModelResults: {
        filterKey: "modelResults",
        itemName: "Model Result",
        valueLabel: "Model Results",
    },
    EndingSentiment: {
        filterKey: "endingSentiment",
        itemName: "Ending Sentiment",
        valueLabel: "Ending Sentiment",
    },
    StartingSentiment: {
        filterKey: "beginningSentiment",
        itemName: "Starting Sentiment",
        valueLabel: "Starting Sentiment",
    },
    HipaaCompliance: {
        filterKey: "hipaaCompliance",
        itemName: "HIPAA Compliance",
        valueLabel: "HIPAA Compliance",
    },
    SafetyEvent: {
        filterKey: "safetyEvent",
        itemName: "Safety Event",
        valueLabel: "Safety Event",
    },
    ExtendedMetadata: {
        fallbackGroupLabel: "Extended Metadata",
        itemName: "Extended Metadata Value",
        valueLabel: "Extended Metadata Values",
    },
    QuestionResult: {
        filterKey: "questionResults",
        itemName: "Question Result",
        valueLabel: "Question Results",
    },
    // Continuous groupings configure buckets rather than picking values, so their item/value labels
    // describe the bucket boundaries their editors collect. They sit beside the filters they share
    // a dimension with rather than in a section of their own.
    Date: {
        filterKey: "dateRange",
        menuTitle: "Date",
        itemName: "Date Grain",
        valueLabel: "Date Grain",
    },
    CallDuration: {
        filterKey: "callDuration",
        itemName: "Duration Range",
        valueLabel: "Duration Ranges",
    },
    MessageCount: {
        filterKey: "messageCount",
        itemName: "Message Count Range",
        valueLabel: "Message Count Ranges",
    },
    // The module score filter's rules come with its entry: the "Module Scores" permission, and
    // Restrict Evaluations hiding it from non-ultra users.
    ModuleScore: {
        filterKey: "moduleScores",
        itemName: "Score Range",
        valueLabel: "Score Ranges",
    },
};

/**
 * Options whose value lists are long enough in practice that the 50-selection notice is worth
 * showing even when the loaded list happens to be short.
 */
export const SELECTION_LIMIT_FALLBACK_OPTIONS =
    new Set<CategoricalGroupByOption>([
        "ModelResults",
        "PrimaryThemes",
        "Themes",
        "PrimaryTopic",
        "PrimaryDriver",
        "ExtendedMetadata",
    ]);
